/**
 * AchievementManager — Persistent achievement tracking via localStorage.
 *
 * 10 achievements:
 *  - explorer:        Explore 100% of a maze's fog
 *  - ghost:           Use Agmuth ghost mode 3 times in a single run
 *  - pray:            Receive 5 Threia blessings (lifetime total)
 *  - rich:            Have 500+ coins in wallet
 *  - survivor:        Reach level 10 on Hard difficulty
 *  - scholar:         Collect all 30 manuscripts
 *  - hoarder:         Collect 100 coins in a single run
 *  - destroyer:       Break 10 walls in a single run
 *  - treasure_hunter: Open 15 chests in a single run
 *  - vorath_defeated: Defeat Vorath in the final battle
 *
 * All functions are named exports. Data is a JSON object in localStorage.
 */

import { ACHIEVEMENTS_STORAGE_KEY } from '../config/game-config.js';

/**
 * Achievement definition list. Order determines display order.
 * @type {ReadonlyArray<{id: string, name: string, description: string}>}
 */
export const ACHIEVEMENT_DEFINITIONS = Object.freeze([
  { id: 'explorer',        name: 'Explorer',        description: 'Explore 100% of a maze' },
  { id: 'ghost',           name: 'Ghost',           description: 'Use Agmuth 3 times in one run' },
  { id: 'pray',            name: 'Devoted',         description: 'Receive 5 Threia blessings' },
  { id: 'rich',            name: 'Rich',            description: 'Accumulate 500 coins in wallet' },
  { id: 'survivor',        name: 'Survivor',        description: 'Reach level 10 on Hard' },
  { id: 'scholar',         name: 'Scholar',         description: 'Collect all 30 manuscripts' },
  { id: 'hoarder',         name: 'Hoarder',         description: 'Collect 100 coins in one run' },
  { id: 'destroyer',       name: 'Destroyer',       description: 'Break 10 walls in one run' },
  { id: 'treasure_hunter', name: 'Treasure Hunter', description: 'Open 15 chests in one run' },
  { id: 'vorath_defeated', name: 'Vorath Defeated', description: 'Defeat Vorath' },
]);

// Valid achievement IDs set for validation
const _VALID_IDS = new Set(ACHIEVEMENT_DEFINITIONS.map((a) => a.id));

/**
 * Load unlocked achievements from localStorage.
 * @returns {Set<string>} Set of unlocked achievement IDs
 */
export function getUnlockedAchievements() {
  try {
    const raw = localStorage.getItem(ACHIEVEMENTS_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      console.warn('AchievementManager: corrupted data, discarding.');
      localStorage.removeItem(ACHIEVEMENTS_STORAGE_KEY);
      return new Set();
    }
    const valid = parsed.filter((id) => typeof id === 'string' && _VALID_IDS.has(id));
    return new Set(valid);
  } catch (err) {
    console.warn('AchievementManager: failed to load:', err);
    return new Set();
  }
}

/**
 * Save unlocked achievements to localStorage.
 * @private
 * @param {Set<string>} unlocked
 */
function _save(unlocked) {
  try {
    const arr = Array.from(unlocked).sort();
    localStorage.setItem(ACHIEVEMENTS_STORAGE_KEY, JSON.stringify(arr));
  } catch (err) {
    console.warn('AchievementManager: failed to save:', err);
  }
}

/**
 * Unlock an achievement. Returns true if newly unlocked, false if already unlocked.
 * @param {string} achievementId
 * @returns {boolean}
 */
export function unlockAchievement(achievementId) {
  if (!_VALID_IDS.has(achievementId)) {
    console.warn(`AchievementManager: unknown achievement "${achievementId}"`);
    return false;
  }
  const unlocked = getUnlockedAchievements();
  if (unlocked.has(achievementId)) return false;
  unlocked.add(achievementId);
  _save(unlocked);
  return true;
}

/**
 * Check if a specific achievement is unlocked.
 * @param {string} achievementId
 * @returns {boolean}
 */
export function isAchievementUnlocked(achievementId) {
  return getUnlockedAchievements().has(achievementId);
}

/**
 * Get the total number of unlocked achievements.
 * @returns {number}
 */
export function getUnlockedCount() {
  return getUnlockedAchievements().size;
}

/**
 * Get the total number of achievements.
 * @returns {number}
 */
export function getTotalCount() {
  return ACHIEVEMENT_DEFINITIONS.length;
}

/**
 * Check if all achievements are unlocked.
 * @returns {boolean}
 */
export function allAchievementsUnlocked() {
  return getUnlockedCount() === getTotalCount();
}
